/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import EmailInput from "../components/auth/EmailInput";
import ErrorMessage from "../components/auth/ErrorMessage";
import UIButton from "../components/Button";
import useSocialAuth from "../hooks/useSocialAuth";
import useEmailAuth from "../hooks/useEmailAuth";
import "../styles/auth.css";
import { css } from "@emotion/react";

type AuthMode = "login" | "register";

const GetStarted = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>("login");
  const [localError, setLocalError] = useState("");

  const { register, login, loading: authLoading, error: authError, clearError } = useEmailAuth();
  const {
    signInWithGoogle,
    signInWithApple,
    loading: socialLoading,
    error: socialError,
    isGoogleEnabled,
    isAppleEnabled,
  } = useSocialAuth();
  const navigate = useNavigate();

  const isLoading = authLoading || socialLoading;
  const displayError = localError || authError || socialError;

  // Password validation
  const isPasswordValid = password.length >= 6;
  const doPasswordsMatch = password === confirmPassword;

  const canSubmit = authMode === "login"
    ? isEmailValid && isPasswordValid
    : isEmailValid && isPasswordValid && doPasswordsMatch;

  const handleSubmit = async () => {
    setLocalError("");
    clearError();

    if (!isEmailValid) {
      setLocalError("Please enter a valid email address.");
      return;
    }

    if (!isPasswordValid) {
      setLocalError("Password must be at least 6 characters.");
      return;
    }

    if (authMode === "register" && !doPasswordsMatch) {
      setLocalError("Passwords do not match.");
      return;
    }

    let result;
    if (authMode === "login") {
      result = await login(email, password);
    } else {
      result = await register(email, password);
    }

    if (result.success) {
      navigate("/");
    }
  };

  const handleGoogleSignIn = async () => {
    const result = await signInWithGoogle();
    if (result.success) {
      navigate("/");
    }
  };

  const handleAppleSignIn = async () => {
    const result = await signInWithApple();
    if (result.success) {
      navigate("/");
    }
  };

  const toggleAuthMode = () => {
    setAuthMode(authMode === "login" ? "register" : "login");
    setLocalError("");
    clearError();
    setPassword("");
    setConfirmPassword("");
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && canSubmit && !isLoading) {
      handleSubmit();
    }
  };

  return (
    <>
      <div className="flex justify-center w-full h-auto">
        <div
          className="auth-container relative flex flex-col items-center justify-center w-full"
          css={css`
            @media (max-width: 1023px) {
              padding: 0;
              margin-top: 40px;
            }
          `}
        >
          <div className="w-full max-w-md flex flex-col">
            <h1 className="text-left mb-6">
              {authMode === "login" ? "Welcome Back" : "Get Started"}
            </h1>

            {/* Email Input */}
            <p className="text-left pl-4 text-white">Email</p>
            <EmailInput
              email={email}
              setEmail={setEmail}
              setIsValid={setIsEmailValid}
              textError={"Invalid Email"}
              onKeyDown={handleKeyDown}
            />

            {/* Password Input */}
            <p className="text-left pl-4 text-white mt-4">Password</p>
            <div className="input-container">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter your password"
                style={{ height: 56, padding: "10px 16px" }}
                className={isPasswordValid ? "valid" : password.length > 0 ? "invalid" : ""}
                disabled={isLoading}
              />
              {password.length > 0 && password.length < 6 && (
                <ErrorMessage message="Password must be at least 6 characters" />
              )}
            </div>

            {/* Confirm Password (Register only) */}
            {authMode === "register" && (
              <>
                <p className="text-left pl-4 text-white mt-4">Confirm Password</p>
                <div className="input-container">
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Confirm your password"
                    style={{ height: 56, padding: "10px 16px" }}
                    className={doPasswordsMatch && confirmPassword.length > 0 ? "valid" : confirmPassword.length > 0 ? "invalid" : ""}
                    disabled={isLoading}
                  />
                  {confirmPassword.length > 0 && !doPasswordsMatch && (
                    <ErrorMessage message="Passwords do not match" />
                  )}
                </div>
              </>
            )}

            {/* Error Message */}
            {displayError && (
              <p className="text-red-500 text-sm mt-3 pl-4">{displayError}</p>
            )}

            {/* Submit Button */}
            <div className="mt-4">
              <UIButton
                text={
                  isLoading
                    ? "Processing..."
                    : authMode === "login"
                    ? "Login"
                    : "Create Account"
                }
                textColor="text-base"
                onClick={handleSubmit}
                backgroundColor="btn-color"
                disabled={!canSubmit || isLoading}
              />
            </div>

            {/* Forgot password (audit C6.3) */}
            {authMode === "login" && (
              <div className="text-center mt-3">
                <button
                  onClick={() => navigate("/forgot-password")}
                  className="text-[#3FD4A2] hover:underline text-sm"
                  disabled={isLoading}
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Toggle Login/Register */}
            <div className="text-center mt-4">
              <span className="text-color-desc">
                {authMode === "login"
                  ? "Don't have an account? "
                  : "Already have an account? "}
              </span>
              <button
                onClick={toggleAuthMode}
                className="text-[#3FD4A2] hover:underline font-medium"
                disabled={isLoading}
              >
                {authMode === "login" ? "Sign Up" : "Login"}
              </button>
            </div>

            {/* Social Login Divider */}
            <div className="flex flex-col items-center mt-6">
              <div className="flex items-center w-full justify-center text-sm mb-4">
                <hr
                  style={{ border: "1px solid #606169" }}
                  className="flex-grow mr-2"
                />
                <span className="text-color-desc">Or continue with</span>
                <hr
                  style={{ border: "1px solid #606169" }}
                  className="flex-grow ml-2"
                />
              </div>

              {/* Social Login Buttons */}
              <div className="flex space-x-4">
                <button
                  onClick={handleGoogleSignIn}
                  disabled={!isGoogleEnabled || isLoading}
                  className={`transition-opacity ${
                    !isGoogleEnabled || isLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:opacity-80"
                  }`}
                  title={
                    isGoogleEnabled
                      ? "Sign in with Google"
                      : "Google Sign-In not configured"
                  }
                >
                  <img
                    src="./assets/images/icon-google.svg"
                    alt="Google"
                    className="w-12 h-12"
                  />
                </button>
                <button
                  onClick={handleAppleSignIn}
                  disabled={!isAppleEnabled || isLoading}
                  className={`transition-opacity ${
                    !isAppleEnabled || isLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:opacity-80"
                  }`}
                  title={
                    isAppleEnabled
                      ? "Sign in with Apple"
                      : "Apple Sign-In not configured"
                  }
                >
                  <img
                    src="./assets/images/icon-apple.svg"
                    alt="Apple"
                    className="w-12 h-12"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className="text-color-desc text-sm my-4 flex justify-between fixed bottom-0 left-0 right-0 p-4 lg:w-1/2 lg:px-8"
        css={css`
          @media (max-width: 767px) {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }
        `}
      >
        <div>
          <span>© 2025 MixxAI</span>
        </div>

        <div>
          <span
            className="underline cursor-pointer"
            onClick={() =>
              window.open(
                "https://witworkapp.com/privacy-policy.html",
                "_blank"
              )
            }
          >
            Privacy Policy
          </span>{" "}
          <span
            className="underline cursor-pointer"
            onClick={() =>
              window.open("https://witworkapp.com/terms-of-use.html", "_blank")
            }
          >
            Terms of Service
          </span>
        </div>
      </div>
    </>
  );
};

export default GetStarted;
